import { useEffect, useState, createContext } from "react";
import { Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import buildUrl from "build-url-ts";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { ReportDatePicker } from "../../components/dateFilter";
import { Moment } from "moment";
import DashboardMetrics from "./components/dashBoard";
import DiscoveryTab from "./components/discovery";
import EngagementTab from "./components/engagement";
import FilterEditor from "./components/entityFilters";
import PerformanceTab from "./components/performance";
import AdvocacyTab from "./components/advocacy";
import { TabBar } from "src/components/tabbar";

import subscriptionPlanService from "src/utils/subscriptionPlan.service";

import {
  daysDifference,
  generateQueryParams,
  parseQueryParams,
} from "src/services/reports.service";
import { useReportsAdoption } from "src/redux/actions/adoption/reports";


import _, { update } from "lodash";
import styles from "./reports.module.scss";
import cx from "classnames";
import { DATE_RANGES } from "src/constants/reports";


export interface Filter {
  _id: string;
  field: string;
  operator: string;
  value: string | number | boolean | null;
  property: string;
}

type TabType = "discovery" | "engagement" | "performance" | "advocacy";

const tabs = ["discovery", "engagement", "performance", "advocacy"];

type reportDateType = {
  type: string | null,
  startDate:  Moment | null,
  endDate:  Moment | null,
};

interface ReportsContextType {
  reportDate: reportDateType | null,
  appliedEntityFilters: string[],
}

export const ReportsContext = createContext<ReportsContextType |null>(null);

const Reports = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryObject = Object.fromEntries(searchParams.entries());

  const isEmployerAdvocacyEnabled = useSelector(state => 
    _.get(state, "accounts.item.employerAdvocacyEnabled", false)
  );

  const { trackReportScreenView, trackReportsViewed } = useReportsAdoption();

  const [activeTab, setActiveTab] = useState<TabType>("discovery");

  const [reportState, setReportState] = useState({
    filter: {
      reportDate: parseQueryParams(queryObject),
      filters: [],
      userType: "all",
      sortBy: "views",
      interval: _.get(queryObject, "interval", "day"),
    },
  });

  const [editor, setEditor] = useState(false);

  const isStarterPlan = subscriptionPlanService.isStarterPlan();

  const generateReportUrl = (url, reportDate, interval) => {
    let updatedUrl = url;
    if (url === "/reports") {
      updatedUrl = "/reports/discovery";
    }

    const queryParams = generateQueryParams(reportDate, interval);

    const query = {
      ...Object.fromEntries(searchParams.entries()),
      ...queryParams,
    };
    const updated = buildUrl(updatedUrl, {
      queryParams: query,
    });

    return updated;
  };

  const handleReportDateChange = (filter) => {
    const reportDate = _.get(filter, "reportDate");
    let daysBetween = -1;

    if (reportDate) {
      daysBetween = daysDifference(
        _.get(reportDate, "startDate"),
        _.get(reportDate, "endDate"),
      );
    }

    const updatedInterval = daysBetween > 365 ? "week" : "day";
    const url = generateReportUrl(
      location.pathname,
      filter.reportDate,
      updatedInterval,
    );

    setReportState({
      ...reportState,
      filter: {
        ...reportState.filter,
        reportDate: {
          ...reportState.filter.reportDate,
          ...reportDate,
        },
        interval: updatedInterval,
      },
    });

    navigate(url);
  };

  const transformFilters = (rawFilters: any[]) => {
    return (rawFilters || []).map(({ property, operator, value = [] }) => ({
      property,
      operator,
      value: Array.isArray(value)
        ? value.map((v) =>
          typeof v === "string"
            ? v
            : property?.type === "company"
            ? v.name
            : v.id
        )
      : [],
    }));
  };

  const handleFilterChange = (filter) => {
    setReportState({
      ...reportState,
      filter: {
        ...reportState.filter,
        filters: filter,
      },
    });
  };

  const handleFilterToggle = () => {
    if (editor) {
      setReportState({
        ...reportState,
        filter: {
          ...reportState.filter,
          filters: [],
        },
      });
      handleFilterChange([]);
    }
    setEditor(!editor);
  };
  

  const handleTimeLineChange = (value) => {
    const updatedInterval = value;
    setReportState({
      ...reportState,
      filter: {
        ...reportState.filter,
        interval: updatedInterval,
      },
    });
    const url = generateReportUrl(
      location.pathname,
      reportState.filter.reportDate,
      updatedInterval,
    );
    navigate(url);
  };

  useEffect(() => {
    setReportState({
      ...reportState,
      filter: {
        ...reportState.filter,
        reportDate: parseQueryParams(location),
        interval: _.get(location, "interval", "day"),
      },
    });
    const url = generateReportUrl(
      location.pathname,
      reportState.filter.reportDate,
      reportState.filter.interval,
    );
    navigate(url);
  }, []);

  useEffect(() => {
    const pathname = location.pathname;
    const path = pathname.split("/").pop();

    if (path === "discovery") {
      setActiveTab("discovery");
    } else if (path === "engagement") {
      setActiveTab("engagement");
    } else if (path === "performance") {
      setActiveTab("performance");
    } else if (path === "advocacy") {
      setActiveTab("advocacy");
    }
  }, [location]);

  useEffect(() => {
    setReportState({
      filter: {
        reportDate: parseQueryParams(queryObject),
        interval: _.get(queryObject, "interval", "day"),
        filters: reportState.filter.filters || [],
        userType: "all",
        sortBy: "views",
      },
    });
  }, [location.search]);

  // Returns unique, non-empty property.type values from applied entity's filters
  const getSelectedEntityArr = (filters: any[]): string[] => {
      const entityList = _.uniq(_.compact(_.map(filters, (item) => _.get(item, "property.type"))));
      return entityList;
  }

  useEffect(() => {
    const source = getSourceFromUrl();
    const sourceCta = _.startCase(source);

    trackReportScreenView({ sourceCta: sourceCta });
    trackReportsViewed({ sourceCta: sourceCta });

  }, []); 
  
  const getSourceFromUrl = (): string => {
    return _.get(location, "query.source", "Direct");
  };

  return (
    <ReportsContext.Provider value={{ reportDate: _.get(reportState,"filter.reportDate", null), appliedEntityFilters: getSelectedEntityArr(reportState.filter.filters) }}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.heading}>my reports</div>
          <div className={styles.description}>Inside scoop on your content</div>

          <DashboardMetrics />

          <div className={styles.filters}>
            <ReportDatePicker
              dateRanges={DATE_RANGES}
              value={reportState.filter.reportDate}
              onChange={handleReportDateChange}
              filter={reportState.filter}
              handleTimeLineChange={handleTimeLineChange}
              className={styles.datePickerFilter}
              showTimelineFilter={true}
            />
            <Dropdown.Toggle
              className={styles.action}
              onClick={handleFilterToggle}
            >
              Any Entity
              <span className={styles.arrow}>
                <i className="paperflite-dropdown" />
              </span>
            </Dropdown.Toggle>

            <FilterEditor
              show={editor}
              filters={reportState.filter.filters || []}
              onChange={handleFilterChange}
            />
          </div>

        <TabBar
          className={cx(styles.tabs, { [styles.bottomOverlay]: !isStarterPlan })}
          gap={60}
          tabSize="equal"
          active={tabs.findIndex((tab) => tab === activeTab)}
          onSelectionChange={(tab) => setActiveTab(tabs[+tab] as TabType)}
        >
          <div
            className={cx(styles.tab, {
              [styles.active]: activeTab === "discovery",
            })}
            onClick={() => {
              navigate(generateReportUrl(
                "/reports/discovery",
                reportState.filter.reportDate,
                _.get(reportState, "filter.interval"),
              ))
            }}
          >
            Discovery
          </div>
         {!isStarterPlan && ( <div
            className={cx(styles.tab, {
              [styles.active]: activeTab === "engagement",
            })}
            onClick={() => {
              navigate(generateReportUrl(
                "/reports/engagement",
                reportState.filter.reportDate,
                _.get(reportState, "filter.interval"),
              ))
            }}
          >
            Engagement
          </div>)}
         { !isStarterPlan &&( <div
            className={cx(styles.tab, {
              [styles.active]: activeTab === "performance",
            })}
            onClick={() => {
              navigate(generateReportUrl(
                "/reports/performance",
                reportState.filter.reportDate,
                _.get(reportState, "filter.interval"),
              ))
            }}
          >
            Performance
          </div>)}
          {!isStarterPlan && isEmployerAdvocacyEnabled && (
            <div
              className={cx(styles.tab, {
                [styles.active]: activeTab === "advocacy",
              })}
              onClick={() => {
                navigate(generateReportUrl(
                  "/reports/advocacy",
                  reportState.filter.reportDate,
                  _.get(reportState, "filter.interval"),
                ))
              }}
            >
              Advocacy
            </div>
          )}
        </TabBar>

        {activeTab === "discovery" && (
          <DiscoveryTab
            reportDate={reportState.filter.reportDate}
            filters={transformFilters(reportState.filter.filters)}
            interval={reportState.filter.interval}
          />
        )}
        {activeTab === "engagement" && (
          <EngagementTab
            reportDate={reportState.filter.reportDate}
            filters={transformFilters(reportState.filter.filters)}
            interval={reportState.filter.interval}
          />
        )}
        {activeTab === "performance" && (
          <PerformanceTab
            reportDate={reportState.filter.reportDate}
            filters={transformFilters(reportState.filter.filters)}
            interval={reportState.filter.interval}
          />
        )}
        {activeTab === "advocacy" && (
          <AdvocacyTab
            reportDate={reportState.filter.reportDate}
            filters={transformFilters(reportState.filter.filters)}
            interval={reportState.filter.interval}
          />
        )}
      </div>
      </div>
    </ReportsContext.Provider>
  );
};

export default Reports;
